import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { MapPin, LocateFixed, Store } from "lucide-react";
import { toast } from "sonner";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const meIcon = L.divIcon({ className:"", html:'<div style="width:16px;height:16px;border-radius:50%;background:#C85A40;box-shadow:0 0 0 6px rgba(200,90,64,.25);border:2px solid #fff"></div>', iconSize:[16,16], iconAnchor:[8,8] });
const MAGELANG = [-7.4699, 110.2177];

function haversine(a, b) {
  const R = 6371, toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b[0]-a[0]), dLng = toRad(b[1]-a[1]);
  const h = Math.sin(dLat/2)**2 + Math.cos(toRad(a[0]))*Math.cos(toRad(b[0]))*Math.sin(dLng/2)**2;
  return 2*R*Math.asin(Math.sqrt(h));
}

function Recenter({ pos, zoom }) {
  const map = useMap();
  useEffect(() => { if (pos) map.setView(pos, zoom || map.getZoom()); }, [pos, zoom, map]);
  return null;
}

export default function MapPage() {
  const [items, setItems] = useState([]);
  const [myPos, setMyPos] = useState(null);
  const [radius, setRadius] = useState(10);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/businesses"); setItems(data); }
    catch (err) { console.error(err); }
  })(); }, []);

  // Auto-request geolocation on mount so map centers on the user immediately
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setMyPos([pos.coords.latitude, pos.coords.longitude]),
      () => { /* user denied — silently keep default center */ },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }, []);

  const withCoords = useMemo(() => items.filter(b => typeof b.lat === "number" && typeof b.lng === "number"), [items]);
  const nearby = useMemo(() => {
    if (!myPos) return [];
    return withCoords.map(b => ({ ...b, distance_km: haversine(myPos, [b.lat, b.lng]) })).filter(b => b.distance_km <= radius).sort((a,b)=>a.distance_km-b.distance_km);
  }, [withCoords, myPos, radius]);

  const useMyLoc = () => {
    if (!navigator.geolocation) { toast.error("Geolokasi tidak tersedia"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setMyPos([pos.coords.latitude, pos.coords.longitude]),
      () => toast.error("Gagal ambil lokasi"),
    );
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Peta Bisnis</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">Jelajahi Magelang</h1>
          <p className="text-bizma-subtext mt-2">{withCoords.length} bisnis di peta{myPos && ` • ${nearby.length} dalam ${radius} km`}.</p>
        </div>
        <button onClick={useMyLoc} className="btn-primary" data-testid="nearby-btn"><LocateFixed size={16}/> {myPos?"Perbarui Lokasi":"Cari Terdekat"}</button>
      </div>

      {myPos && (
        <div className="felt-card p-4 mb-4 flex items-center gap-4" data-testid="radius-control">
          <MapPin className="text-bizma-terracotta" size={18}/>
          <div className="text-sm text-bizma-subtext w-24">Radius {radius} km</div>
          <input type="range" min="1" max="50" value={radius} onChange={(e)=>setRadius(parseInt(e.target.value,10))} className="flex-1 accent-bizma-terracotta" data-testid="radius-slider" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="felt-card overflow-hidden lg:col-span-2" data-testid="map-container">
          <div className="h-[35vh] lg:h-[70vh]">
            <MapContainer center={myPos || MAGELANG} zoom={myPos ? 14 : 12} style={{height:"100%",width:"100%"}} scrollWheelZoom>
              <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {myPos && <Recenter pos={myPos} zoom={14}/>}
              {myPos && <><Marker position={myPos} icon={meIcon}><Popup>Lokasi Anda</Popup></Marker>
                <Circle center={myPos} radius={radius*1000} pathOptions={{color:"#C85A40",weight:1,fillOpacity:0.05}}/></>}
              {(myPos ? nearby : withCoords).map(b => (
                <Marker key={b.business_id} position={[b.lat, b.lng]} icon={icon}>
                  <Popup>
                    <div style={{minWidth:180}}>
                      {b.photo_path && <img src={fileUrl(b.photo_path)} alt="" style={{width:"100%",height:100,objectFit:"cover",borderRadius:8}}/>}
                      <div style={{fontWeight:700,marginTop:6}}>{b.name}</div>
                      <div style={{fontSize:12,color:"#73706A"}}>{b.category} • {b.owner_name}</div>
                      {b.distance_km !== undefined && <div style={{fontSize:12,color:"#7BA082"}}>{b.distance_km.toFixed(2)} km dari Anda</div>}
                      <Link to={`/app/businesses/${b.business_id}`} style={{color:"#C85A40",fontSize:13,marginTop:6,display:"inline-block"}}>Lihat detail →</Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
        <div className="felt-card p-6" data-testid="nearby-list">
          <h2 className="font-display font-semibold text-lg">{myPos?"Bisnis Terdekat":"Semua Bisnis"}</h2>
          <div className="mt-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {(myPos?nearby:withCoords).length === 0 && <div className="text-sm text-bizma-subtext">Belum ada bisnis dengan koordinat.</div>}
            {(myPos?nearby:withCoords).map(b => (
              <Link key={b.business_id} to={`/app/businesses/${b.business_id}`} className="flex items-center gap-3 border border-bizma-border rounded-2xl p-3 hover:bg-bizma-muted transition-colors" data-testid={`nearby-item-${b.business_id}`}>
                <div className="w-14 h-14 rounded-xl bg-bizma-muted overflow-hidden shrink-0">
                  {b.photo_path ? <img src={fileUrl(b.photo_path)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full grid place-items-center text-bizma-subtext"><Store size={16}/></div>}
                </div>
                <div className="flex-1 min-w-0"><div className="font-medium truncate">{b.name}</div><div className="text-xs text-bizma-subtext">{b.category}</div></div>
                {b.distance_km !== undefined && <div className="text-xs text-bizma-sage font-semibold">{b.distance_km.toFixed(1)} km</div>}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
